// src/components/common/FiscalYearSelectionModal.js
import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, CheckCircle } from 'lucide-react';

const FiscalYearSelectionModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  fiscalYears = [], 
  isLoading = false,
  title = "Select Fiscal Year",
  documentType = null 
}) => {
  const modalRef = useRef(null);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  
  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);
  
  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFiscalYear(null);
    }
  }, [isOpen]);
  
  // Close the modal when clicking outside the content
  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };
  
  const handleFiscalYearSelect = (fiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
  };
  
  const handleConfirmSelection = () => {
    if (selectedFiscalYear) {
      onSelect(selectedFiscalYear);
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-96 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-600">Loading fiscal years...</span>
            </div>
          ) : fiscalYears.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No fiscal years available</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-4">
                Please select a fiscal year to generate the {documentType} document:
              </p>
              {fiscalYears
                .filter((fiscalYear) => {
                  if (documentType === 'P60') {
                    return fiscalYear.active === false && fiscalYear.allowP60Print === true;
                  }
                  return true;
                })
                .map((fiscalYear) => (
                <button
                  key={fiscalYear.id}
                  onClick={() => handleFiscalYearSelect(fiscalYear)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedFiscalYear?.id === fiscalYear.id
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-900'
                      : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{fiscalYear.fiscalYear}</div>
                      <div className="text-sm text-gray-500">
                        {fiscalYear.startDate} to {fiscalYear.endDate}
                      </div>
                    </div>
                    {selectedFiscalYear?.id === fiscalYear.id && (
                      <CheckCircle className="h-5 w-5 text-indigo-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmSelection}
            disabled={!selectedFiscalYear || isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiscalYearSelectionModal;